/**
 * @fileoverview Unified Global Audio Redux Slice
 *
 * Single source of truth for ALL audio playback across the application.
 * Replaces both waveformAudioSlice and audioPlayerSlice.
 *
 * **Supported Player Types:**
 * - WaveSurfer: Waveform visualization player
 * - Howler: Howler.js audio player
 * - Native: HTML5 audio elements
 *
 * **Features:**
 * - Single active audio enforcement
 * - Play/pause/stop state tracking
 * - Volume and playback rate control
 * - Loading and time tracking
 * - Automatic coordination across all player types
 *
 * @module store/slices/globalAudioSlice
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export type AudioPlayerType = 'wavesurfer' | 'howler' | 'native'

export interface GlobalAudioState {
  /** Unique ID of the currently active audio (song ID, flashcard ID, etc.) */
  currentAudioId: string | null
  /** Currently playing audio URL */
  currentAudioUrl: string | null
  /** Type of player currently active */
  playerType: AudioPlayerType | null
  /** Whether audio is currently playing */
  isPlaying: boolean
  /** Whether audio is loading */
  isLoading: boolean
  /** Volume (0-1) */
  volume: number
  /** Playback rate (0.5-2.0) */
  playbackRate: number
  /** Current playback time in seconds */
  currentTime: number
  /** Total duration in seconds */
  duration: number
}

const initialState: GlobalAudioState = {
  currentAudioId: null,
  currentAudioUrl: null,
  playerType: null,
  isPlaying: false,
  isLoading: false,
  volume: 0.7,
  playbackRate: 1.0,
  currentTime: 0,
  duration: 0,
}

const globalAudioSlice = createSlice({
  name: 'globalAudio',
  initialState,
  reducers: {
    /**
     * Start playing audio (automatically stops any other playing audio)
     */
    playAudio: (
      state,
      action: PayloadAction<{
        audioUrl: string
        audioId: string
        playerType: AudioPlayerType
      }>
    ) => {
      state.currentAudioUrl = action.payload.audioUrl
      state.currentAudioId = action.payload.audioId
      state.playerType = action.payload.playerType
      state.isPlaying = true
      state.isLoading = false
    },

    /**
     * Start loading audio
     */
    loadAudio: (
      state,
      action: PayloadAction<{
        audioUrl: string
        audioId: string
        playerType: AudioPlayerType
      }>
    ) => {
      state.currentAudioUrl = action.payload.audioUrl
      state.currentAudioId = action.payload.audioId
      state.playerType = action.payload.playerType
      state.isLoading = true
      state.isPlaying = false
      state.currentTime = 0
      state.duration = 0
    },

    /**
     * Audio loaded successfully
     */
    audioLoaded: (state, action: PayloadAction<{ duration: number }>) => {
      state.duration = action.payload.duration
      state.isLoading = false
    },

    /**
     * Audio load failed
     */
    audioLoadFailed: (state) => {
      state.isLoading = false
    },

    /**
     * Pause current audio (keeps position)
     */
    pauseAudio: (state) => {
      state.isPlaying = false
    },

    /**
     * Resume paused audio
     */
    resumeAudio: (state) => {
      if (state.currentAudioUrl) {
        state.isPlaying = true
      }
    },

    /**
     * Stop and clear current audio
     */
    stopAudio: (state) => {
      state.currentAudioUrl = null
      state.currentAudioId = null
      state.playerType = null
      state.isPlaying = false
      state.currentTime = 0
      state.duration = 0
    },

    /**
     * Set volume (0-1)
     */
    setVolume: (state, action: PayloadAction<number>) => {
      state.volume = Math.max(0, Math.min(1, action.payload))
    },

    /**
     * Set playback rate (0.5-2.0)
     */
    setPlaybackRate: (state, action: PayloadAction<number>) => {
      state.playbackRate = Math.max(0.5, Math.min(2.0, action.payload))
    },

    /**
     * Update current playback time
     */
    setCurrentTime: (state, action: PayloadAction<number>) => {
      state.currentTime = action.payload
    },

    /**
     * Update duration
     */
    setDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload
    },

    /**
     * Audio ended (reset position, keep loaded)
     */
    audioEnded: (state) => {
      state.isPlaying = false
      state.currentTime = 0
    },
  },
})

export const {
  playAudio,
  loadAudio,
  audioLoaded,
  audioLoadFailed,
  pauseAudio,
  resumeAudio,
  stopAudio,
  setVolume,
  setPlaybackRate,
  setCurrentTime,
  setDuration,
  audioEnded,
} = globalAudioSlice.actions

export default globalAudioSlice.reducer

/** Selectors */
export const selectCurrentAudioUrl = (state: { globalAudio: GlobalAudioState }) =>
  state.globalAudio.currentAudioUrl
export const selectCurrentAudioId = (state: { globalAudio: GlobalAudioState }) =>
  state.globalAudio.currentAudioId
export const selectPlayerType = (state: { globalAudio: GlobalAudioState }) =>
  state.globalAudio.playerType
export const selectIsPlaying = (state: { globalAudio: GlobalAudioState }) =>
  state.globalAudio.isPlaying
export const selectIsLoading = (state: { globalAudio: GlobalAudioState }) =>
  state.globalAudio.isLoading
export const selectVolume = (state: { globalAudio: GlobalAudioState }) =>
  state.globalAudio.volume
export const selectPlaybackRate = (state: { globalAudio: GlobalAudioState }) =>
  state.globalAudio.playbackRate
export const selectCurrentTime = (state: { globalAudio: GlobalAudioState }) =>
  state.globalAudio.currentTime
export const selectDuration = (state: { globalAudio: GlobalAudioState }) =>
  state.globalAudio.duration

/** Helper selector - check if a specific audio is currently playing */
export const selectIsAudioPlaying =
  (audioId: string) => (state: { globalAudio: GlobalAudioState }) =>
    state.globalAudio.currentAudioId === audioId && state.globalAudio.isPlaying

/** Helper selector - check if a specific audio is active (may be paused) */
export const selectIsAudioActive =
  (audioId: string) => (state: { globalAudio: GlobalAudioState }) =>
    state.globalAudio.currentAudioId === audioId
